export function planCoverage({ storyHeight, cardHeights, viewportHeight, headerHeight = 0, wholeSet = false, minimum = 3 }) {
  const heights = cardHeights.map(Number);
  if (!Number.isFinite(storyHeight) || storyHeight <= 0 || !heights.length || heights.some(h => !Number.isFinite(h) || h <= 0)) return null;
  let count = 0;
  let height = 0;
  for (const cardHeight of heights) {
    if (!wholeSet && count >= minimum && height >= storyHeight) break;
    height += cardHeight;
    count += 1;
  }
  const shortfall = Math.max(0, storyHeight - height);
  const average = height / count;
  const header = Math.max(16, Math.min(headerHeight + 16, viewportHeight / 3));
  return {
    count,
    height,
    shortfall,
    coverage: Math.min(1, height / storyHeight),
    additionalEstimate: shortfall > 1 ? Math.ceil(shortfall / average) : 0,
    sticky: shortfall > 1 && Number.isFinite(viewportHeight) && viewportHeight > 0,
    stickyTop: Math.min(header, viewportHeight - height - 16)
  };
}

if (typeof document !== 'undefined') {
  const controllers = new Map();
  class ArticleCoverage {
    constructor(layout) {
      this.layout = layout;
      this.story = layout.querySelector('.rc-variety-story');
      this.rails = [...layout.querySelectorAll('.rc-variety-rail')];
      this.schedule = this.schedule.bind(this);
      this.frame = 0;
      if (!this.story || !this.rails.length) return;
      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(this.schedule);
        this.observer.observe(this.story);
        this.rails.forEach(rail => this.observer.observe(rail));
      }
      window.addEventListener('resize', this.schedule, { passive: true });
      this.layout.addEventListener('load', this.schedule, true);
      document.fonts?.ready.then(this.schedule);
      if (layout.dataset.rcEditor === 'true') {
        this.report = document.createElement('p');
        this.report.className = 'rc-coverage-editor-report page-width';
        this.report.setAttribute('aria-live', 'polite');
        layout.after(this.report);
      }
      this.schedule();
    }
    schedule() {
      if (!this.frame && this.layout.isConnected) this.frame = requestAnimationFrame(() => { this.frame = 0; this.measure(); });
    }
    measure() {
      const desktop = window.matchMedia('(min-width:1100px)').matches;
      const storyHeight = this.story.getBoundingClientRect().height;
      const header = document.querySelector('sticky-header, .shopify-section-header-sticky');
      const headerHeight = header?.getBoundingClientRect().height || 0;
      const viewportHeight = window.innerHeight;
      const messages = [];
      this.layout.dataset.rcStoryHeight = String(Math.round(storyHeight));
      for (const rail of this.rails) {
        const cards = [...rail.querySelectorAll('.rc-variety-card')];
        if (!desktop) {
          cards.forEach(card => card.removeAttribute('data-rc-desktop-hidden'));
          rail.removeAttribute('data-rc-sticky');
          rail.style.removeProperty('--rc-sticky-top');
          rail.dataset.rcCoverageState = 'mobile-story-first';
          continue;
        }
        const width = rail.getBoundingClientRect().width;
        const heights = cards.map((card, index) => {
          const ratio = Number(card.dataset.rcArtworkRatio);
          const margin = index ? parseFloat(getComputedStyle(card).marginTop) || 0 : 0;
          return width / ratio + margin;
        });
        const plan = planCoverage({ storyHeight, cardHeights: heights, viewportHeight, headerHeight, wholeSet: rail.dataset.rcSetPolicy === 'whole' });
        if (!plan) {
          cards.forEach(card => card.removeAttribute('data-rc-desktop-hidden'));
          rail.removeAttribute('data-rc-sticky');
          rail.dataset.rcCoverageState = 'measurement-unavailable';
          continue;
        }
        cards.forEach((card, index) => card.toggleAttribute('data-rc-desktop-hidden', index >= plan.count));
        rail.toggleAttribute('data-rc-sticky', plan.sticky);
        rail.style.setProperty('--rc-sticky-top', Math.round(plan.stickyTop) + 'px');
        rail.dataset.rcCoverageState = plan.shortfall > 1 ? 'creative-shortage' : 'complete';
        rail.dataset.rcVisibleCards = String(plan.count);
        rail.dataset.rcStackHeight = String(Math.round(plan.height));
        rail.dataset.rcShortfallPx = String(Math.round(plan.shortfall));
        rail.dataset.rcAdditionalPanelsEstimate = String(plan.additionalEstimate);
        rail.dataset.rcStaticCoverage = plan.coverage.toFixed(3);
        if (this.report) {
          const values = { brand: rail.dataset.rcBrand, cards: plan.count, remaining: Math.round(plan.shortfall), estimate: plan.additionalEstimate };
          messages.push((this.layout.dataset.rcCoverageLabel || '').replace(/\{(brand|cards|remaining|estimate)\}/g, (_, key) => String(values[key])));
        }
      }
      if (this.report) this.report.textContent = desktop ? messages.join(' ') : '';
    }
    destroy() {
      this.observer?.disconnect();
      window.removeEventListener('resize', this.schedule);
      this.layout.removeEventListener('load', this.schedule, true);
      if (this.frame) cancelAnimationFrame(this.frame);
      this.report?.remove();
    }
  }
  function connect() {
    for (const [layout, controller] of controllers) {
      if (!layout.isConnected) { controller.destroy(); controllers.delete(layout); }
    }
    document.querySelectorAll('[data-rc-coverage]').forEach(layout => {
      if (!controllers.has(layout)) controllers.set(layout, new ArticleCoverage(layout));
    });
  }
  connect();
  document.addEventListener('shopify:section:load', connect);
  document.addEventListener('shopify:section:unload', event => {
    for (const [layout, controller] of controllers) {
      if (event.target.contains(layout)) { controller.destroy(); controllers.delete(layout); }
    }
  });
}
